// v10/src/components/MaintenanceBanner.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Info, Wrench } from 'lucide-react';

const TYPE_CONFIG = {
  warning:     { bg: 'rgba(217,119,6,0.95)',     border: 'rgba(245,158,11,0.4)', icon: AlertTriangle, emoji: '⚠️', label: 'Aviso' },
  maintenance: { bg: 'rgba(239,68,68,0.95)',      border: 'rgba(239,68,68,0.4)',  icon: Wrench,        emoji: '🔧', label: 'Mantenimiento' },
  info:        { bg: 'rgba(37,99,235,0.95)',       border: 'rgba(59,130,246,0.4)', icon: Info,          emoji: 'ℹ️', label: 'Información' },
};

const MaintenanceBanner = ({ orgId }) => {
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    // El banner se monta sólo para usuarios logueados (ver App.js, post-isAuthenticated).
    // Adjuntamos el access token si existe — mismo storage que apiService.js — para
    // evitar el 401 en loop cuando el backend valida auth en /api/maintenance/active.
    const getAuthHeader = () => {
      try {
        const raw =
          localStorage.getItem('condor360_current_user') ||
          sessionStorage.getItem('condor360_current_user');
        const user = raw ? JSON.parse(raw) : null;
        return user?.token ? { Authorization: `Bearer ${user.token}` } : {};
      } catch { return {}; }
    };

    const fetchNotice = async () => {
      try {
        const API = process.env.REACT_APP_API_URL || 'https://condor360-backend.onrender.com';
        const url = orgId
          ? `${API}/api/maintenance/active?org_id=${orgId}`
          : `${API}/api/maintenance/active`;
        const res  = await fetch(url, { headers: getAuthHeader() });
        if (!res.ok) {
          setNotice(null);
          return;
        }
        const data = await res.json();
        if (data.success && data.notice) {
          const key = `condor360_notice_dismissed_${data.notice.id}`;
          // Solo mostrar si no fue descartado en esta sesión
          if (!sessionStorage.getItem(key)) {
            setNotice(data.notice);
          } else {
            // Si el aviso fue descartado pero cambió (nueva id), limpiar y mostrar
            setNotice(null);
          }
        } else {
          // No hay aviso activo — limpiar
          setNotice(null);
        }
      } catch (_) {}
    };

    fetchNotice();
    // Polling cada 30s — más reactivo ante cambios del SuperAdmin
    const t = setInterval(fetchNotice, 30000);
    return () => clearInterval(t);
  }, [orgId]);

  const handleDismiss = () => {
    if (notice) {
      // Guardar en sessionStorage con el ID del aviso
      // Si el SuperAdmin crea un NUEVO aviso, tendrá distinto ID y volverá a mostrarse
      sessionStorage.setItem(`condor360_notice_dismissed_${notice.id}`, '1');
    }
    setNotice(null);
  };

  if (!notice) return null;

  const cfg   = TYPE_CONFIG[notice.type] || TYPE_CONFIG.warning;
  const Icon  = cfg.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -60 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -60 }}
        transition={{ type: 'spring', stiffness: 200, damping: 22 }}
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          zIndex: 999,
          background: cfg.bg,
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${cfg.border}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        {/* Ícono */}
        <div style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 10, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon style={{ width: 16, height: 16, color: 'white' }} />
        </div>

        {/* Mensaje */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: 'white', letterSpacing: '-0.2px' }}>
            {cfg.emoji} {cfg.label}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>
            {notice.message}
          </p>
          {notice.ends_at && (
            <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>
              Hasta: {new Date(notice.ends_at).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          )}
        </div>

        {/* Cerrar */}
        <motion.button
          onClick={handleDismiss}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <X style={{ width: 14, height: 14, color: 'white' }} />
        </motion.button>
      </motion.div>
    </AnimatePresence>
  );
};

export default MaintenanceBanner;
